import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid } from "recharts";
import { format } from "date-fns";
import { getExerciseProgressSeries } from "../lib/progressInsightsUtils";
import NewColors from "../../../shared/theme/NewColors";

/** Responsive chart dimensions: optional full width for grid layouts. */
function getChartContainerSx(fullWidth) {
  if (fullWidth) {
    return {
      flexShrink: 0,
      minWidth: 0,
      width: "100%",
      height: { xs: 200, sm: 220 },
    };
  }
  return {
    flexShrink: 0,
    minWidth: 0,
    width: { xs: "100%", sm: 300 },
    height: { xs: 180, sm: 200 },
    maxWidth: { sm: 300 },
  };
}

const getChartFrameSx = (theme) => ({
  border: `1px solid ${theme.palette.primary.main}26`,
  borderRadius: 2,
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  backgroundColor: NewColors.cardBackground,
});

/**
 * Placeholder shown when there's insufficient data for the progress chart.
 * Same dimensions as the chart for consistent layout.
 */
function ChartPlaceholder({ theme, fullWidth }) {
  return (
    <Box
      sx={{
        ...getChartContainerSx(fullWidth),
        ...getChartFrameSx(theme),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderStyle: "dashed",
        backgroundColor: NewColors.sectionBackground,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        Need more data
      </Typography>
    </Box>
  );
}

/**
 * Line chart: max weight per workout day for one exercise (same 90-day window as progress insights).
 * Shows a placeholder when fewer than 2 data points.
 */
function ExerciseProgressChart({
  exerciseId,
  aggregated,
  weightUnit = "lbs",
  fullWidth = false,
}) {
  const theme = useTheme();
  const lineColor = theme.palette.primary.main;
  const series = React.useMemo(
    () => getExerciseProgressSeries(aggregated ?? [], exerciseId),
    [aggregated, exerciseId]
  );

  const chartData = React.useMemo(
    () =>
      series.map((r) => ({
        date: r.date,
        label: format(r.date instanceof Date ? r.date : new Date(r.date), "M/d"),
        value: r.maxWeight ?? 0,
      })),
    [series]
  );

  if (!chartData || chartData.length < 2) {
    return <ChartPlaceholder theme={theme} fullWidth={fullWidth} />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ ...getChartContainerSx(fullWidth), ...getChartFrameSx(theme), p: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={`${lineColor}14`} vertical={false} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: NewColors.text.secondary }}
            />
            <YAxis
              dataKey="value"
              width={32}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: NewColors.text.secondary }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              strokeWidth={2}
              dot={{ fill: lineColor, strokeWidth: 1.5, r: 3, stroke: NewColors.cardBackground }}
              activeDot={{ r: 4, fill: lineColor, stroke: NewColors.cardBackground, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.75, textAlign: { xs: "center", sm: "left" }, fontWeight: 500 }}
      >
        Max weight ({weightUnit}) · last 90 days
      </Typography>
    </Box>
  );
}

export default ExerciseProgressChart;
